import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IDictionary } from '@core/common.model';
import { DateFormatService } from '@core/date/date-format.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { InitialConfigService } from '../app-config/initial-config.service';
import { IDateProduct } from './models/date-product.model';
import { IProductAvailabilityIndex } from './models/product-availability-index.model';
import { IProductInfo } from './models/product-info.model';
import { IProductSession } from './models/product-session.model';
import { CSessionBasedProductTypes } from './models/product-type.type';
import { IProduct } from './models/product.model';
import { ProductHelperService } from './product-helper.service';
import { IRestrictedAddons } from './models/restricted-addons.model';
import { IOrderItem } from '@core/order/models/order-item.model';
import { getAnimationDuration } from '../animation/animation';
import { ProductAvailableHelperService } from '@core/product/product-available-helper.service';
import { ProductAvailableService } from '@core/product/product-available.service';
import { ProductListService } from '@core/product/product-list.service';

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private productAvailability$: BehaviorSubject<IDictionary<IDateProduct>> = new BehaviorSubject<
    IDictionary<IDateProduct>
  >({});

  readonly delayTimer: number;

  constructor(
    private http: HttpClient,
    private initialConfigService: InitialConfigService,
    private dateFormatService: DateFormatService,
    private productHelperService: ProductHelperService,
    private productAvailableHelperService: ProductAvailableHelperService,
    private productAvailableService: ProductAvailableService,
    private productListService: ProductListService
  ) {
    this.delayTimer = getAnimationDuration(100);
  }

  /** Return cached list of venue products */
  getProductList(): Observable<IProduct[]> {
    return this.productListService.getParentProductList$();
  }

  getProductListSync(): IProduct[] {
    return this.productListService.getParentProductList();
  }

  getParentProductAvailability(parentProductId: string, bookingDate: Date): IProduct | undefined {
    const dateString = this.dateFormatService.dateToStringDateOnlyHyphen(bookingDate);
    const productDictionary = this.productAvailability$.value;
    return productDictionary[dateString]?.products.find((product) => product.id === parentProductId);
  }

  /** Set via Venue-Config, shouldn't need to reset this */
  setProductList(products: IProduct[]): void {
    this.productListService.setProductList(products);
  }

  /**
   * If there is no data in the product config for the queried date, then fetches the config from API.
   * Else returns what is in the BehaviorSubject.
   * @returns Observable of the product configuration array.
   */
  getProductAvailability(date: Date | string | undefined, bookingUniqueId?: string | null): Observable<IProduct[]> {
    const dateString = this.dateFormatService.dateToStringDateOnlyHyphen(date || new Date());

    return this.productAvailability$.asObservable().pipe(
      // take(1) so that when setProductConfig.next occurs this doesn't re-emit.
      take(1),
      switchMap((productDictionary: IDictionary<IDateProduct>) => {
        if (
          // eslint-disable-next-line no-prototype-builtins
          productDictionary.hasOwnProperty(dateString) &&
          this.productHelperService.isQueryFresh(productDictionary[dateString].queryCompleted)
        ) {
          return of([...productDictionary[dateString].products] as IProduct[]);
        }

        return this.fetchProductAvailability(dateString, bookingUniqueId, productDictionary);
      }),
      catchError((err: unknown) => {
        console.error('Error fetching product availability', err);
        return of([]);
      })
    );
  }

  /** When a product has been purchased it should clear the Product Availability. */
  clearProductAvailability(): void {
    this.productAvailability$.next({});
  }

  /**
   * Gets the availability for a date.
   * Returns the both the date and the items in an object for use in the addon's selection component calculation.
   */
  getProductAvailabilityDictionary(dateIndex: string): Observable<IProductAvailabilityIndex> {
    return this.getProductAvailability(dateIndex).pipe(
      take(1),
      map((items: IProduct[]) => {
        return { dateIndex, items };
      })
    );
  }

  /**
   * Gets the basic product configuration elements from the API and announces to the BehaviorSubject.
   * @param date expecting a sql format date string - '2021-10-30'.
   * @returns Observable of the Product Configuration from the API.
   */
  fetchProductAvailability(
    date: string,
    bookingUniqueId: string | undefined | null,
    productDictionary: IDictionary<IDateProduct>
  ): Observable<IProduct[]> {
    const config = this.initialConfigService.getInitialConfig();
    let url = `${config.baseApiUrl}/checkout/${config.checkoutSlug}/product-availability?date=${date}`;
    if (bookingUniqueId) {
      url = url + `&bookingUniqueId=${bookingUniqueId}`;
    }

    return this.http.get<IProduct[]>(url).pipe(
      catchError((err: unknown) => {
        console.error('error fetching the availability', err);
        return of([]);
      }),
      switchMap((availability: IProduct[]) => this.mergedProducts(availability)),
      map((products: IProduct[]) => {
        this.productAvailableHelperService.storeProductAvailabilityLookup(products, date);
        products = this.addProductStates(products, date);
        productDictionary[date] = { queryCompleted: new Date(), products };
        this.productAvailability$.next(productDictionary);

        return products;
      })
    );
  }

  private mergedProducts(availability: IProduct[]): Observable<IProduct[]> {
    return this.getProductList().pipe(
      map((products: IProduct[]) => this.productHelperService.mergeProductsAvailabilities(availability, products)),
      map((products: IProduct[]) => this.mergePackageSessions(products))
    );
  }

  /** Once the base products availabilities have been merged, we can then merge the sessions to their packages */
  mergePackageSessions(allProducts: IProduct[]): IProduct[] {
    return allProducts.map((product) => {
      if (product.packageSubType?.includes('sessionpass') || product.packageSubType?.includes('recurringpass')) {
        const relatedSessionProducts = this.productHelperService.getAvailablePackageSessions(
          product.products,
          allProducts
        );
        const sessions = this.productHelperService.getPackageUniqueSessions(relatedSessionProducts);
        return { ...product, sessions };
      }

      return product;
    });
  }

  /** Determines on load if onlineSalesClosed or product isSoldOut */
  addProductStates(allProducts: IProduct[], date: string): IProduct[] {
    return allProducts.map((product: IProduct) => {
      if (product.type === 'package') {
        return this.calculatePackageItems(product, date);
      }

      if (product.isItemSessionBased) {
        return this.calculateSessionItems(product, product.sessions);
      }

      return this.calculateStandardProduct(product, date);
    });
  }

  /** Stock standard pass's, memberships etc - usually has a straight forward pathway. */
  private calculateStandardProduct(product: IProduct, dateString: string): IProduct {
    product.isSoldOut = this.calculateSoldOut(product, dateString, [], false, [], undefined);
    product.isOnlineSalesClosed = product.availabilities?.every((available) => !available.onlineSalesOpen);
    return product;
  }

  /** If all sessions are sold out or closed then assign to the parent product. */
  private calculateSessionItems(product: IProduct, sessions: IProductSession[] | undefined): IProduct {
    product.isSoldOut = this.productHelperService.isAllSessionSoldOut(product.products, sessions);
    product.isOnlineSalesClosed = this.productHelperService.isOnlineSalesClosed(sessions, undefined, undefined, false);

    return product;
  }

  /** Packages have to be linked to their packageItem tickets first before being assessed */
  private calculatePackageItems(product: IProduct, dateString: string): IProduct {
    if (product.packageSubType?.includes('sessionpass') || product.packageSubType?.includes('recurringpass')) {
      return this.calculateSessionItems(product, product.sessions);
    }

    return this.calculateStandardProduct(product, dateString);
  }

  /**
   * Get's the checkouts next available purchase date from the API
   * @param date object or string converted to API expected string - '2021-10-30'.
   * @returns Observable of the next available date mapped to a Date object
   */
  fetchNextProductAvailabilityDate(
    date: Date | string | undefined,
    productId?: number | string
  ): Observable<Date | undefined> {
    if (!date) {
      return of();
    }

    date = this.dateFormatService.dateToStringDateOnlyHyphen(date);
    let url = `/product-availability/next-available?startDate=${date}`;
    if (productId) {
      url = `${url}&productId=${productId}`;
    }

    const config = this.initialConfigService.getInitialConfig();
    return this.http.get<string>(`${config.baseApiUrl}/checkout/${config.checkoutSlug}${url}`).pipe(
      map((date: string) => this.dateFormatService.dateIndexToDate(date)),
      catchError(() => of(undefined))
    );
  }

  /** Checks that the current addon being checked is not a forced purchase and is not an addon with userDefinedCosts and has availability */
  filterAddonList(
    addon: IProduct,
    addonIdToShow: string[], // filter on config based on product or addonPage
    dateHyphenString: string,
    productAvailabilities: IProduct[],
    basket: IOrderItem[],
    isEditBooking: boolean,
    restrictedAddons: IRestrictedAddons[],
    selectedSession: IProductSession | undefined,
    includeForcedAddons = false
  ): boolean {
    if (
      !addonIdToShow.includes(addon.id) ||
      (!includeForcedAddons && this.productHelperService.isForcedPurchaseAddon(addon))
    ) {
      return false;
    }

    if (addon.type === 'addon' && addon.products.every((product: IProductInfo) => product.hasUserDefinedCost)) {
      return false;
    }

    const findAddonAvailability = productAvailabilities.find((availability: IProduct) => availability.id === addon.id);
    if (!findAddonAvailability || !findAddonAvailability.products.length) {
      return false;
    }

    if (addon.type === 'package' && addon.packageSubType?.some((item) => CSessionBasedProductTypes.includes(item))) {
      const packageSessions = this.productHelperService.getAvailablePackageSessions(
        addon.products,
        productAvailabilities
      );
      return packageSessions.some((linkedProduct: IProduct) => {
        if (!linkedProduct.products.length) {
          return false;
        }

        return !this.calculateSoldOut(
          linkedProduct,
          dateHyphenString,
          basket,
          isEditBooking,
          restrictedAddons,
          selectedSession
        );
      });
    }

    return !this.calculateSoldOut(
      findAddonAvailability,
      dateHyphenString,
      basket,
      isEditBooking,
      restrictedAddons,
      selectedSession
    );
  }

  /**
   * Uses Max add to calculate the parent product sold out status by determining if every ticket/variation is not addable
   * If a session is passed - only calculates availability / restricted addon on that session.
   */
  calculateSoldOut(
    productWithAvailability: IProduct,
    dateHyphenString: string,
    basket: IOrderItem[],
    isEditBooking: boolean,
    restrictedAddons: IRestrictedAddons[],
    selectedSession: IProductSession | undefined
  ): boolean {
    const date = this.dateFormatService.dateStringToDate(dateHyphenString);

    return productWithAvailability.products.every((checkProduct: IProductInfo) => {
      if (productWithAvailability.sessions) {
        return productWithAvailability.sessions.every((session) => {
          return !this.productAvailableService.canAddProduct(checkProduct, basket, date, session, isEditBooking);
        });
      }

      return !this.productAvailableService.canAddProduct(
        checkProduct,
        basket,
        date,
        selectedSession,
        isEditBooking,
        restrictedAddons
      );
    });
  }
}
